import { memo, useCallback, useRef, useState } from 'react'
import {
    PolygonF,
    GoogleMap,
    MarkerF,
    useJsApiLoader,
    Libraries,
    PolylineF,
} from '@react-google-maps/api'
import { Box, Button, Typography } from '@mui/material'
import { googleMapOptions } from '../../constants/constants'
import { useTranslation } from 'react-i18next'

interface MapContainerPolygonProps {
    markerPosition: any
    onPolygonDrawn?: (latLngs: number[][]) => void
    onPolygonEdited?: (latLngs: number[][]) => void
    polygon: number[][]
    zoom: number
    setZoom?: any
    editable?: boolean
}

const libraries: Libraries = ['places']

const MapContainerPolygon = (props: MapContainerPolygonProps) => {
    const { t } = useTranslation()
    const {
        markerPosition,
        onPolygonDrawn = () => undefined,
        onPolygonEdited = () => undefined,
        polygon,
        zoom,
        setZoom,
        editable = false,
    } = props
    const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''
    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries,
    })

    const mapRef = useRef<google.maps.Map | null>(null)
    const [isDrawing, setIsDrawing] = useState(false)
    const [draftPoints, setDraftPoints] = useState<google.maps.LatLngLiteral[]>(
        [],
    )

    const onLoad = useCallback(function callback(map: google.maps.Map) {
        mapRef.current = map
    }, [])

    const onUnmount = useCallback(function callback() {
        mapRef.current = null
    }, [])

    const handlePolygonEdit = useCallback(
        (polygon: google.maps.Polygon) => {
            const paths = polygon
                .getPath()
                .getArray()
                .map((point) => [point.lng(), point.lat()])
            onPolygonEdited(paths)
        },
        [onPolygonEdited],
    )

    const handleZoomChanged = useCallback(() => {
        if (mapRef.current) {
            const newZoom = mapRef.current.getZoom()
            setZoom?.(newZoom)
        }
    }, [setZoom])

    const handleMapClick = useCallback(
        (e: google.maps.MapMouseEvent) => {
            if (!isDrawing || !e.latLng) return
            setDraftPoints((prev) => [
                ...prev,
                { lat: e.latLng!.lat(), lng: e.latLng!.lng() },
            ])
        },
        [isDrawing],
    )

    const startDrawing = useCallback(() => {
        setDraftPoints([])
        setIsDrawing(true)
    }, [])

    const cancelDrawing = useCallback(() => {
        setDraftPoints([])
        setIsDrawing(false)
    }, [])

    const savePolygon = useCallback(() => {
        if (draftPoints.length >= 3) {
            onPolygonDrawn(draftPoints.map((p) => [p.lng, p.lat]))
        }
        setDraftPoints([])
        setIsDrawing(false)
    }, [draftPoints, onPolygonDrawn])

    const formattedPolygon = polygon?.map((point) => ({
        lat: point[1],
        lng: point[0],
    }))

    const draftPolylinePath =
        draftPoints.length > 0 ? [...draftPoints, draftPoints[0]] : []

    return isLoaded ? (
        <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
            {editable && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 10,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 10,
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                    }}
                >
                    {!isDrawing ? (
                        <Button
                            variant="contained"
                            size="small"
                            onClick={startDrawing}
                            sx={{
                                backgroundColor: '#fff',
                                color: '#333',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                '&:hover': { backgroundColor: '#f5f5f5' },
                                textTransform: 'none',
                                fontSize: '13px',
                            }}
                        >
                            {t('drawPolygon')}
                        </Button>
                    ) : (
                        <>
                            <Typography
                                sx={{
                                    backgroundColor: '#fff',
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: '4px',
                                    fontSize: '13px',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                    color: '#333',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {t('clickToAddPoints')}
                            </Typography>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={savePolygon}
                                disabled={draftPoints.length < 3}
                                sx={{
                                    backgroundColor: '#1976d2',
                                    color: '#fff',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                    '&:hover': { backgroundColor: '#1565c0' },
                                    '&:disabled': { backgroundColor: '#ccc' },
                                    textTransform: 'none',
                                    fontSize: '13px',
                                }}
                            >
                                {t('save')}
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={cancelDrawing}
                                sx={{
                                    backgroundColor: '#fff',
                                    color: '#d32f2f',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                    '&:hover': { backgroundColor: '#ffeaea' },
                                    textTransform: 'none',
                                    fontSize: '13px',
                                }}
                            >
                                {t('cancel')}
                            </Button>
                        </>
                    )}
                </Box>
            )}
            <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={markerPosition || { lat: 36.778259, lng: -119.417931 }}
                zoom={zoom}
                onLoad={onLoad}
                onUnmount={onUnmount}
                onZoomChanged={handleZoomChanged}
                onClick={handleMapClick}
                options={{
                    ...googleMapOptions,
                    draggableCursor: isDrawing ? 'crosshair' : undefined,
                }}
            >
                {markerPosition && <MarkerF position={markerPosition} />}
                {polygon?.length > 0 && (
                    <PolygonF
                        draggable={editable}
                        paths={formattedPolygon}
                        editable={editable}
                        onEdit={handlePolygonEdit}
                        options={{
                            fillColor: '#F76565',
                            strokeColor: '#B42929',
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                        }}
                    />
                )}
                {isDrawing && draftPoints.length > 0 && (
                    <>
                        {draftPoints.map((point, i) => (
                            <MarkerF
                                key={i}
                                position={point}
                                options={{
                                    icon: {
                                        path: google.maps.SymbolPath.CIRCLE,
                                        scale: 5,
                                        fillColor: '#B42929',
                                        fillOpacity: 1,
                                        strokeColor: '#fff',
                                        strokeWeight: 1,
                                    },
                                }}
                            />
                        ))}
                        {draftPolylinePath.length > 1 && (
                            <PolylineF
                                path={draftPolylinePath}
                                options={{
                                    strokeColor: '#B42929',
                                    strokeOpacity: 0.8,
                                    strokeWeight: 2,
                                }}
                            />
                        )}
                    </>
                )}
            </GoogleMap>
        </Box>
    ) : (
        <Typography>{t('loadingmap')}</Typography>
    )
}

const MemoizedMapContainerPolygon = memo(MapContainerPolygon)

export default MemoizedMapContainerPolygon
